import {
    BehaviorSubject,
    Observable,
    combineLatest,
    delay,
    fromEvent,
} from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";
import Siema, { SiemaOptions } from "siema";

import type SliderControllerInterface from "./SliderControllerInterface";

export default class SiemaSliderController
    implements SliderControllerInterface
{
    constructor(public resizeDelay: number = 0) {}
    public siema: Siema | null = null;
    private slidesCount$ = new BehaviorSubject<number | null>(null);
    public slideIndex$ = new BehaviorSubject<number | null>(null);
    public connected$ = new BehaviorSubject(false);
    public currentPerPage$ = new BehaviorSubject<number | null>(null);
    public sliderElements$ = new BehaviorSubject<HTMLElement[] | null>(null);

    selectSlidesCount(): Observable<number> {
        this.checkSiemaInstance();
        return this.slidesCount$.pipe(
            filter((v: number | null): v is number => v !== null)
        );
    }

    selectCurrentSlideIndex(): Observable<number> {
        this.checkSiemaInstance();
        return this.slideIndex$.pipe(
            filter((v: number | null): v is number => v !== null)
        );
    }

    selectCurrentPerPage(): Observable<number> {
        this.checkSiemaInstance();
        return this.currentPerPage$.pipe(
            filter((v: number | null): v is number => v !== null)
        );
    }

    getSliderElements(): HTMLElement[] | null {
        this.checkSiemaInstance();
        return this.sliderElements$.getValue();
    }

    next(countToScroll: number | undefined = undefined): void {
        this.checkSiemaInstance();
        this.siema?.next(countToScroll || this.siema?.perPage);
    }

    prev(countToScroll: number | undefined = undefined): void {
        this.checkSiemaInstance();
        this.siema?.prev(countToScroll || this.siema?.perPage);
    }

    goToIndex(idx: number): void {
        this.checkSiemaInstance();
        this.siema?.goTo(idx);
    }

    private updateHandler(siema: Siema): void {
        const slidesCount =
            (siema as unknown as { innerElements: HTMLElement[] }).innerElements
                .length || 0;
        const currentSlide = siema.currentSlide;
        const perPage = (siema as unknown as { perPage: number }).perPage;

        this.slidesCount$.next(slidesCount);
        this.slideIndex$.next(currentSlide);
        this.currentPerPage$.next(perPage);
        this.sliderElements$.next(
            (siema as unknown as { innerElements: HTMLElement[] }).innerElements
        );
    }

    offResizeOnFullScreen(): void {
        window.removeEventListener(
            "resize",
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
            (this.siema as any)?.resizeHandler
        );
        fromEvent(window, "resize")
            .pipe(delay(this.resizeDelay))
            .subscribe(() => {
                const isFullscreen = document.fullscreenElement;
                if (!isFullscreen) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call
                    (this.siema as any)?.resizeHandler();
                }
            });
    }

    connect(
        target: HTMLElement,
        options: Omit<SiemaOptions, "onInit" | "onChange">
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const _this = this;
        this.siema = new Siema({
            selector: target,
            ...options,
            startIndex: this.slideIndex$.getValue() || options.startIndex,
            onChange() {
                _this.updateHandler(this as Siema);
            },
            onInit() {
                _this.updateHandler(this as Siema);
            },
        });
        this.connected$.next(true);
        this.offResizeOnFullScreen();
    }

    selectHasPrev() {
        return this.connected$.pipe(
            filter((v) => !!v),
            switchMap(() => this.selectCurrentSlideIndex()),
            map((currentIndex) => {
                if (currentIndex === 0) {
                    return false;
                }

                return true;
            })
        );
    }

    selectHasNext() {
        return this.connected$.pipe(
            filter((v) => !!v),
            switchMap(() =>
                combineLatest([
                    this.selectSlidesCount(),
                    this.selectCurrentSlideIndex(),
                    this.selectCurrentPerPage(),
                ])
            ),
            map(([slidesCount, currentIndex, currentPerPage]) => {
                if (!slidesCount || slidesCount <= currentPerPage) {
                    return false;
                }

                return currentIndex < slidesCount - currentPerPage;
            })
        );
    }

    destroy(): void {
        this.siema?.destroy();
    }

    checkSiemaInstance(): void {
        if (!this.siema) {
            throw new Error(
                "This SiemaSliderController is not connected to Siema object"
            );
        }
    }
}
